import { Trash2 } from 'lucide-react';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';

import { Button } from '@@/buttons';
import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Link } from '@@/Link';

import { NamespaceSelect } from '../rbac/NamespaceSelect';

import type { KubernetesRegistryFormValues } from './types';

const accessStorageKey = 'k8s-registry-access-table';
const accessSettingsStore = createPersistedStore(accessStorageKey);

type AccessRow = KubernetesRegistryFormValues['accessRows'][0] & {
  registryName?: string;
};

const columnHelper = createColumnHelper<AccessRow>();

interface RegistryAccessDatatableProps {
  formData: { accessRows: KubernetesRegistryFormValues['accessRows'] };
  onChange: (
    key: 'accessRows',
    value: KubernetesRegistryFormValues['accessRows']
  ) => void;
}

export function KubernetesRegistryAccessDatatable({
  formData,
  onChange,
}: RegistryAccessDatatableProps) {
  const { accessRows } = formData;
  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });
  const registries = registriesQuery.data || [];

  const tableState = useTableState(accessSettingsStore, accessStorageKey);

  // Map registry IDs to names
  const registryMap = new Map(registries.map((r) => [r.Id, r.Name]));

  // Add registry names to rows for display
  const rowsWithNames: AccessRow[] = accessRows.map((row) => ({
    ...row,
    registryName: registryMap.get(row.registryId) || 'Unknown',
  }));

  const availableNamespaces = useMemo(
    () => Array.from(new Set(accessRows.flatMap((a) => a.namespaces || []))),
    [accessRows]
  );

  return (
    <Datatable
      title="Registry access list"
      getRowId={(row) => String(row.registryId)}
      dataset={rowsWithNames}
      columns={getColumns(handleNamespacesChange, availableNamespaces)}
      settingsManager={tableState}
      isRowSelectable={() => true}
      renderTableActions={(selectedRows) => (
        <TableActions selectedRows={selectedRows} onRemove={handleRemoveRows} />
      )}
      data-cy="k8s-registry-access-datatable"
      emptyContentLabel="No registry access configured"
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function handleRemoveRows(items: AccessRow[]) {
    const idsToRemove = new Set(items.map((item) => item.registryId));
    const newAccessRows = accessRows.filter(
      (row) => !idsToRemove.has(row.registryId)
    );
    onChange('accessRows', newAccessRows);
  }

  function handleNamespacesChange(item: AccessRow, namespaces: string[]) {
    const newAccessRows = [...accessRows];
    const index = newAccessRows.findIndex(
      (row) => row.registryId === item.registryId
    );
    if (index !== -1) {
      newAccessRows[index] = { ...newAccessRows[index], namespaces };
      onChange('accessRows', newAccessRows);
    }
  }
}

function TableActions({
  selectedRows,
  onRemove,
}: {
  selectedRows: AccessRow[];
  onRemove: (items: AccessRow[]) => void;
}) {
  return (
    <Button
      color="dangerlight"
      size="small"
      icon={Trash2}
      onClick={() => onRemove(selectedRows)}
      data-cy="remove-registry-access-button"
      disabled={selectedRows.length === 0}
    >
      Remove
    </Button>
  );
}

function getColumns(
  handleNamespacesChange: (item: AccessRow, namespaces: string[]) => void,
  availableNamespaces: string[]
) {
  return [
    columnHelper.accessor('registryName', {
      header: 'Registry',
      cell: ({ row }) => (
        <Link
          to={`/admin/registries/${row.original.registryId}`}
          data-cy={`registry-link-${row.original.registryId}`}
        >
          {row.original.registryName || 'Unknown'}
        </Link>
      ),
    }),
    columnHelper.accessor('namespaces', {
      header: 'Namespaces',
      cell: ({ row }) => (
        <NamespaceSelect
          selectedNamespaces={row.original.namespaces || []}
          onChange={(namespaces) =>
            handleNamespacesChange(row.original, namespaces)
          }
          placeholder="e.g. default, production"
          data-cy={`namespaces-input-${row.original.registryId}`}
          namespaces={availableNamespaces}
        />
      ),
    }),
  ];
}
